import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChannelEvent } from '../../lib/events.js';

interface EventFeedProps {
  events: ChannelEvent[];
}

function tierLabel(tier: string): string {
  if (tier === '2000') return ' (Tier 2)';
  if (tier === '3000') return ' (Tier 3)';
  return '';
}

function EventLine({ ev }: { ev: ChannelEvent }) {
  if (ev.type === 'follow') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-pink-400">♥</span>
        <span className="text-pink-300 font-semibold">{ev.displayName}</span>
        <span className="text-slate-400">folgt jetzt</span>
      </div>
    );
  }

  if (ev.type === 'subscribe') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-purple-400">★</span>
        <span className="text-purple-300 font-semibold">{ev.displayName}</span>
        <span className="text-slate-400">
          {ev.isGift ? 'Sub-Geschenk erhalten' : `abonniert${tierLabel(ev.tier)}`}
        </span>
      </div>
    );
  }

  if (ev.type === 'resub') {
    const months = ev.streakMonths != null
      ? `${ev.cumulativeMonths} Monate (${ev.streakMonths} in Folge)`
      : `${ev.cumulativeMonths} Monate`;
    return (
      <div className="flex flex-col px-2.5 py-[3px] border-b border-slate-800/60 text-xs gap-[1px]">
        <div className="flex items-center gap-[5px]">
          <span className="text-purple-400">★</span>
          <span className="text-purple-300 font-semibold">{ev.displayName}</span>
          <span className="text-slate-400">resub{tierLabel(ev.tier)} — {months}</span>
        </div>
        {ev.message && (
          <div className="text-slate-400 pl-4 italic truncate">„{ev.message}"</div>
        )}
      </div>
    );
  }

  if (ev.type === 'gift_sub') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-purple-400">🎁</span>
        <span className="text-purple-300 font-semibold">
          {ev.isAnonymous ? 'Anonym' : ev.displayName}
        </span>
        <span className="text-slate-400">
          verschenkt {ev.total} {ev.total === 1 ? 'Abo' : 'Abos'}{tierLabel(ev.tier)}
        </span>
      </div>
    );
  }

  if (ev.type === 'cheer') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-yellow-400">◆</span>
        <span className="text-yellow-300 font-semibold">
          {ev.isAnonymous ? 'Anonym' : ev.displayName}
        </span>
        <span className="text-slate-400">{ev.bits.toLocaleString('de-DE')} Bits</span>
        {ev.message && (
          <span className="text-slate-500 truncate max-w-[120px]">{ev.message}</span>
        )}
      </div>
    );
  }

  if (ev.type === 'redemption') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-cyan-400">⚡</span>
        <span className="text-cyan-300 font-semibold">{ev.displayName}</span>
        <span className="text-slate-400 truncate">{ev.rewardTitle}</span>
        <span className="text-slate-600">({ev.rewardCost.toLocaleString('de-DE')} P.)</span>
      </div>
    );
  }

  if (ev.type === 'shop_redemption') {
    return (
      <div className="flex flex-col px-2.5 py-[3px] border-b border-slate-800/60 text-xs gap-[1px]">
        <div className="flex items-center gap-[5px]">
          <span className="text-emerald-400">🎯</span>
          <span className="text-emerald-300 font-semibold">{ev.user}</span>
          <span className="text-slate-400">{ev.isRaffle ? 'hat sich angemeldet' : 'hat eingelöst'}</span>
          <span className="text-slate-600">({ev.cost.toLocaleString('de-DE')} P.)</span>
        </div>
        <div className="text-slate-300 pl-4 break-words">{ev.itemName}</div>
      </div>
    );
  }

  if (ev.type === 'raid') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-orange-400">⚔</span>
        <span className="text-orange-300 font-semibold">{ev.fromDisplayName}</span>
        <span className="text-slate-400">raidet mit</span>
        <span className="text-orange-200 font-semibold">{ev.viewers.toLocaleString('de-DE')}</span>
        <span className="text-slate-400">Zuschauern</span>
      </div>
    );
  }

  if (ev.type === 'viewer_milestone') {
    return (
      <div className="flex flex-col px-2.5 py-[3px] border-b border-slate-800/60 text-xs gap-[1px]">
        <div className="flex items-center gap-[5px]">
          <span className="text-amber-400">🔥</span>
          <span className="text-amber-300 font-semibold">{ev.displayName || ev.username}</span>
          <span className="text-slate-400">
            Zuschauer-Serie: {ev.streakCount} Streams
          </span>
        </div>
        {ev.message && (
          <div className="text-slate-400 pl-4 italic truncate">„{ev.message}"</div>
        )}
      </div>
    );
  }

  if (ev.type === 'sub_share_message') {
    return (
      <div className="flex flex-col px-2.5 py-[3px] border-b border-slate-800/60 text-xs gap-[1px]">
        <div className="flex items-center gap-[5px]">
          <span className="text-purple-400">★</span>
          <span className="text-purple-300 font-semibold">{ev.displayName || ev.username}</span>
          <span className="text-slate-400">abonniert</span>
        </div>
        <div className="text-slate-400 pl-4 italic truncate">„{ev.message}"</div>
      </div>
    );
  }

  if (ev.type === 'mode_change') {
    const labels: string[] = [];
    const m = ev.modes;
    if (m.slow_mode !== undefined)
      labels.push(m.slow_mode ? `Slow (${m.slow_mode_wait_time ?? ''}s)` : 'Slow aus');
    if (m.subscriber_mode !== undefined)
      labels.push(m.subscriber_mode ? 'Sub-Only an' : 'Sub-Only aus');
    if (m.emote_mode !== undefined)
      labels.push(m.emote_mode ? 'Emote-Only an' : 'Emote-Only aus');
    if (m.follower_mode !== undefined)
      labels.push(m.follower_mode
        ? `Follower-Only an${m.follower_mode_duration ? ` (${m.follower_mode_duration}min)` : ''}`
        : 'Follower-Only aus');
    if (m.unique_chat_mode !== undefined)
      labels.push(m.unique_chat_mode ? 'Unique-Chat an' : 'Unique-Chat aus');
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-blue-400">⚙</span>
        <span className="text-blue-300 font-semibold">{ev.modBy}</span>
        <span className="text-slate-400">{labels.join(', ')}</span>
      </div>
    );
  }

  if (ev.type === 'kofi_donation') {
    return (
      <div className="flex flex-col px-2.5 py-[3px] border-b border-slate-800/60 text-xs gap-[1px]">
        <div className="flex items-center gap-[5px]">
          <span className="text-[#FF5E5B]">&#9749;</span>
          <span className="font-semibold text-[#FF8C8A]">{ev.from}</span>
          <span className="text-slate-400">spendet {ev.amount} {ev.currency}</span>
        </div>
        {ev.message && (
          <div className="text-slate-400 pl-4 italic truncate">„{ev.message}"</div>
        )}
      </div>
    );
  }

  if (ev.type === 'announcement') {
    const colorMap: Record<string, string> = {
      primary: 'text-violet-300',
      blue: 'text-blue-300',
      green: 'text-green-300',
      orange: 'text-orange-300',
      purple: 'text-purple-300',
    };
    const accent = colorMap[ev.color] ?? 'text-violet-300';
    return (
      <div className="flex flex-col px-2.5 py-[3px] border-b border-slate-800/60 text-xs gap-[1px]">
        <div className="flex items-center gap-[5px]">
          <span className={accent}>📢</span>
          <span className={`${accent} font-semibold`}>{ev.displayName || 'Ankündigung'}</span>
          <span className="text-slate-400">kündigt an</span>
        </div>
        {ev.message && (
          <div className="text-slate-300 pl-4 truncate">{ev.message}</div>
        )}
      </div>
    );
  }

  if (ev.type === 'gift_sub_recipient') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-purple-400">🎁</span>
        <span className="text-purple-300 font-semibold">
          {ev.isAnonymous ? 'Anonym' : ev.gifterDisplayName}
        </span>
        <span className="text-slate-400">schenkt</span>
        <span className="text-purple-200 font-semibold">{ev.recipientDisplayName}</span>
        <span className="text-slate-400">ein Abo{tierLabel(ev.tier)}</span>
      </div>
    );
  }

  if (ev.type === 'kofi_subscription') {
    return (
      <div className="flex items-center gap-[5px] px-2.5 py-[3px] text-xs border-b border-slate-800/60">
        <span className="text-[#FF5E5B]">&#9749;</span>
        <span className="font-semibold text-[#FF8C8A]">{ev.from}</span>
        <span className="text-slate-400">unterstützt monatlich</span>
        <span className="text-slate-500">{ev.amount} {ev.currency}</span>
      </div>
    );
  }

  return null;
}

const STORAGE_KEY = 'event-feed-height';
const DEFAULT_HEIGHT = 76;
const MIN_HEIGHT = 44;
const MAX_HEIGHT = 500;
const LONG_PRESS_DELAY = 150;
const SCROLL_CANCEL_THRESHOLD = 5;

function clamp(v: number) {
  return Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, v));
}

function loadHeight(): number {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) return clamp(Number(v));
  } catch { /* ignore */ }
  return DEFAULT_HEIGHT;
}

export default function EventFeed({ events }: EventFeedProps) {
  const [height, setHeight] = useState(loadHeight);
  const [dragReady, setDragReady] = useState(false);
  const dragging = useRef(false);
  const dragActive = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAtBottom = useRef(true);

  function onScroll() {
    const el = containerRef.current;
    if (!el) return;
    isAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;
  }

  useLayoutEffect(() => {
    if (isAtBottom.current && containerRef.current) {
      const el = containerRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [events]);

  useEffect(() => {
    function onMove(e: MouseEvent | TouchEvent) {
      if (!dragging.current) return;
      const clientY = 'touches' in e ? (e.touches[0]?.clientY ?? startY.current) : e.clientY;
      if (!dragActive.current) {
        // Finger moved too far before long-press fired → scroll gesture, cancel drag
        if (Math.abs(clientY - startY.current) > SCROLL_CANCEL_THRESHOLD) {
          if (longPressTimer.current !== null) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
          }
          dragging.current = false;
        }
        return;
      }
      const next = clamp(startH.current + (clientY - startY.current));
      setHeight(next);
    }
    function onUp() {
      if (longPressTimer.current !== null) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      if (!dragging.current) return;
      const wasDragging = dragActive.current;
      dragging.current = false;
      dragActive.current = false;
      setDragReady(false);
      setHeight((h) => {
        try { localStorage.setItem(STORAGE_KEY, String(h)); } catch { /* ignore */ }
        return h;
      });
      // After resize: scroll to newest event so it isn't hidden behind the handle
      if (wasDragging && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      if (longPressTimer.current !== null) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };
  }, []);

  function onHandleDown(e: React.MouseEvent | React.TouchEvent) {
    dragging.current = true;
    dragActive.current = false;
    startY.current = 'touches' in e ? (e.touches[0]?.clientY ?? 0) : e.clientY;
    startH.current = height;
    if ('preventDefault' in e) e.preventDefault();

    // Long-press: activate drag only after 150ms hold
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
    }
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      dragActive.current = true;
      setDragReady(true);
    }, LONG_PRESS_DELAY);
  }

  return (
    <div
      className="flex-shrink-0 overflow-hidden bg-slate-900/60 border-b border-slate-800 flex flex-col"
      style={{ height }}
    >
      {/* scrollable events area — flex-1 + min-h-0 so it never overflows the outer clip boundary */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto min-h-0"
        onScroll={onScroll}
      >
        {/* inner wrapper fills at least full scroll area so events anchor to bottom */}
        <div className="flex flex-col justify-end" style={{ minHeight: '100%' }}>
          {events.length === 0 ? (
            <div className="flex items-center justify-center py-2 text-xs text-slate-600 select-none">
              Keine Events
            </div>
          ) : (
            events.map((ev, i) => (
              <EventLine key={`${ev.type}-${ev.receivedAt}-${i}`} ev={ev} />
            ))
          )}
        </div>
      </div>
      {/* Resize handle — minimal: outer div hugs the colored bar (kein großer Leerraum
          mehr). Bar selbst leicht erhöht für Greifbarkeit auf Touch.
          NOT sticky: lives as a normal flex child at the bottom of the outer column so
          the outer overflow:hidden is the hard clip and events never bleed past it. */}
      <div
        className="group flex-shrink-0 h-2 flex items-center cursor-ns-resize select-none"
        onMouseDown={onHandleDown}
        onTouchStart={onHandleDown}
        title="Höhe anpassen"
      >
        <div className={`w-full h-2 transition-colors ${dragReady ? 'bg-purple-600' : 'bg-slate-800 group-hover:bg-purple-700/70 group-active:bg-purple-600'}`} />
      </div>
    </div>
  );
}
